import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
    motion,
    useMotionValueEvent,
    useReducedMotion,
    useScroll,
} from "framer-motion";
import "./FormatsSection.css";

// Single config array: edit formats and video/poster files here.
const FORMATS = [
    {
        id: "longform",
        name: "YouTube Long-Forms",
        ratio: "16:9",
        width: 1920,
        height: 1080,
        description:
            "Full edits built around story, pacing and retention for the main channel.",
        bullets: ["Talking head, docs, tutorials", "Chapters, b-roll, graphics"],
        video: "/videos/longform.mp4",
        poster: "/videos/longform.jpg",
    },
    {
        id: "shorts",
        name: "YouTube Shorts",
        ratio: "9:16",
        width: 1080,
        height: 1920,
        description:
            "Vertical cuts that hold the first second and pull viewers to the main channel.",
        bullets: ["Under 60 seconds", "Captions and hooks"],
        video: "/videos/shorts.mp4",
        poster: "/videos/shorts.jpg",
    },
    {
        id: "reels",
        name: "Instagram Reels",
        ratio: "9:16",
        width: 1080,
        height: 1920,
        description:
            "Reels cut for the feed, with on-screen text that works without sound.",
        bullets: ["Safe zones for UI", "Cover frame"],
        video: "/videos/reels.mp4",
        poster: "/videos/reels.jpg",
    },
    {
        id: "tiktok",
        name: "TikTok Videos",
        ratio: "9:16",
        width: 1080,
        height: 1920,
        description: "Native-feeling edits paced for the For You page.",
        bullets: ["Fast hooks", "Trend-aware cuts"],
        video: "/videos/tiktok.mp4",
        poster: "/videos/tiktok.jpg",
    },
    {
        id: "facebook",
        name: "Facebook 1:1 Videos",
        ratio: "1:1",
        width: 1080,
        height: 1080,
        description:
            "Square video that reads well on desktop and mobile feeds.",
        bullets: ["Square safe layout", "Text-first openings"],
        video: "/videos/facebook.mp4",
        poster: "/videos/facebook.jpg",
    },
];

const STEP_COUNT = FORMATS.length;

// Fallback to WebM (VP8) for rare browsers without H.264 support.
const SUPPORTS_H264 =
    typeof document !== "undefined" &&
    document
        .createElement("video")
        .canPlayType('video/mp4; codecs="avc1.42E01E"') !== "";

const withFallback = (mp4) =>
    SUPPORTS_H264 ? mp4 : mp4.replace(/\.mp4$/, ".webm");

export const FormatsSection = () => {
    const reduce = useReducedMotion();
    const trackRef = useRef(null);
    const bezelRef = useRef(null);
    const stageRef = useRef(null);
    const stepRef = useRef(0);
    const [step, setStep] = useState(0);
    const [flash, setFlash] = useState(false);
    const [screen, setScreen] = useState({ width: 320, height: 180 });

    const { scrollYProgress } = useScroll({
        target: trackRef,
        offset: ["start start", "end end"],
    });

    useMotionValueEvent(scrollYProgress, "change", (v) => {
        const next = Math.min(
            STEP_COUNT - 1,
            Math.max(0, Math.floor(v * STEP_COUNT)),
        );
        if (next !== stepRef.current) {
            stepRef.current = next;
            setStep(next);
            if (!reduce) {
                setFlash(true);
                setTimeout(() => setFlash(false), 150);
            }
        }
    });

    useLayoutEffect(() => {
        const measure = () => {
            const bezel = bezelRef.current?.getBoundingClientRect();
            if (!bezel) return;
            const maxW = bezel.width * 0.9;
            const maxH = bezel.height * 0.86;
            const f = FORMATS[stepRef.current];
            const r = f.width / f.height;
            let w = maxW;
            let h = w / r;
            if (h > maxH) {
                h = maxH;
                w = h * r;
            }
            setScreen({ width: Math.round(w), height: Math.round(h) });
        };
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, [step]);

    useEffect(() => {
        const videos = stageRef.current?.querySelectorAll("video") || [];
        videos.forEach((v, i) => {
            if (i === step) v.play().catch(() => {});
            else v.pause();
        });
    }, [step]);

    const active = FORMATS[step];
    const side = step % 2 === 0 ? "left" : "right";

    return (
        <section
            className="formats"
            id="services"
            data-testid="formats-section"
        >
            <h2 className="formats-heading" data-testid="formats-heading">
                YouTube growth systems and post-production for creators and
                founders.
            </h2>

            <div className="formats-track" ref={trackRef}>
                <div className="formats-sticky" ref={stageRef}>
                    <div className="fmt-chrome">
                        <span data-testid="fmt-chrome-left">
                            / GROWCO // FORMATS
                        </span>
                        <span className="fmt-chrome-center">
                            SCROLL TO SWITCH CHANNEL
                        </span>
                        <span data-testid="fmt-chrome-ratio">
                            / RATIO // {active.ratio}
                        </span>
                    </div>

                    <div className="formats-stage">
                        <div className="fmt-slot">
                            {side === "left" && (
                                <FormatCard
                                    format={active}
                                    step={step}
                                    reduce={reduce}
                                />
                            )}
                        </div>

                        <div
                            className="fmt-monitor"
                            data-testid="fmt-monitor"
                        >
                            <div className="fmt-case">
                                <div className="fmt-bezel" ref={bezelRef}>
                                    <div
                                        className="fmt-screen"
                                        style={{
                                            width: `${screen.width}px`,
                                            height: `${screen.height}px`,
                                        }}
                                        data-testid="fmt-screen"
                                    >
                                        {FORMATS.map((f, i) => (
                                            <video
                                                key={f.id}
                                                className={`fmt-video${i === step ? " is-active" : ""}`}
                                                src={
                                                    i <= step + 1
                                                        ? withFallback(f.video)
                                                        : undefined
                                                }
                                                poster={f.poster}
                                                muted
                                                loop
                                                playsInline
                                                autoPlay
                                                preload="metadata"
                                                aria-label={`${f.name} sample edit`}
                                            />
                                        ))}
                                        <div className="fmt-scanlines" />
                                        <div className="fmt-vignette" />
                                        <div
                                            className={`fmt-flash${flash ? " on" : ""}`}
                                        />
                                    </div>
                                </div>
                                <div className="fmt-chin">
                                    <span className="fmt-grill" />
                                    <span className="fmt-brand">GROWCO</span>
                                </div>
                            </div>
                            <div className="fmt-base" />
                            <div className="fmt-table" />
                        </div>

                        <div className="fmt-slot">
                            {side === "right" && (
                                <FormatCard
                                    format={active}
                                    step={step}
                                    reduce={reduce}
                                />
                            )}
                        </div>
                    </div>

                    <div className="fmt-counter" data-testid="fmt-counter">
                        CH <strong>{step + 1}</strong> / {STEP_COUNT}
                    </div>
                </div>
            </div>
        </section>
    );
};

const FormatCard = ({ format, step, reduce }) => {
    const side = step % 2 === 0 ? "left" : "right";
    return (
        <motion.aside
            key={step}
            className={`fmt-card fmt-card-${side}`}
            initial={
                reduce
                    ? false
                    : {
                          opacity: 0,
                          x: side === "left" ? -48 : 48,
                          scale: 0.9,
                      }
            }
            animate={{ opacity: 1, x: 0, scale: 1 }}
            transition={{ duration: 0.45, ease: "easeOut" }}
            data-testid="fmt-card"
        >
            <span className="fmt-card-meta">
                {String(step + 1).padStart(2, "0")} · {format.ratio} ·{" "}
                {format.width} × {format.height}
            </span>
            <h3 className="fmt-card-name">{format.name}</h3>
            <p className="fmt-card-desc">{format.description}</p>
            <ul className="fmt-card-bullets">
                {format.bullets.map((b) => (
                    <li key={b}>{b}</li>
                ))}
            </ul>
        </motion.aside>
    );
};
